import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Component, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useAuthStore } from "./stores/authStore";
import { api } from "./lib/apiClient";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import SignupEducationPage from "./pages/SignupEducationPage";
import DisclosuresPage from "./pages/DisclosuresPage";
import DataSafetyPage from "./pages/DataSafetyPage";
import AgentsPage from "./pages/AgentsPage";
import XRayPage from "./pages/XRayPage";
import ArtifactsPage from "./pages/ArtifactsPage";
import MemoryPage from "./pages/MemoryPage";
import AgentCreatorPage from "./pages/AgentCreatorPage";
import AgentEditorPage from "./pages/AgentEditorPage";
import ProfilePage from "./pages/ProfilePage";
import GuidePage from "./pages/GuidePage";
import OnboardingPage from "./pages/OnboardingPage";
import { usePipelineEvents } from "./hooks/usePipelineEvents";
import { useIdleTimeout } from "./hooks/useIdleTimeout";
import { useLocaleInit } from "./hooks/useLocaleInit";
import { useGlobalCommandShortcut } from "./hooks/useGlobalCommandShortcut";
import { CommandPaletteModal } from "./components/chat/CommandPaletteModal";
class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, message: "" };
    }
    static getDerivedStateFromError(error) {
        return {
            hasError: true,
            message: error instanceof Error ? error.message : "An unexpected error occurred.",
        };
    }
    render() {
        if (this.state.hasError) {
            return (_jsxs("div", { style: { padding: "2rem", color: "#e57373", fontFamily: "monospace" }, children: [_jsx("h2", { children: "Something went wrong." }), _jsx("p", { children: this.state.message }), _jsx("button", { onClick: () => this.setState({ hasError: false, message: "" }), children: "Try again" })] }));
        }
        return this.props.children;
    }
}
function RequireAuth({ children }) {
    const authed = useAuthStore((s) => !!s.accessToken);
    return authed ? children : _jsx(Navigate, { to: "/login", replace: true });
}
// Gate all protected pages on onboarding_complete. First fetch of /profile/me
// tells us whether the user has finished intake. Until the fetch resolves we
// render nothing to avoid a flash of the target page.
function RequireOnboarded({ children }) {
    const location = useLocation();
    const [status, setStatus] = useState("loading");
    useEffect(() => {
        let cancelled = false;
        api
            .me()
            .then((p) => {
            if (cancelled)
                return;
            setStatus(p.onboarding_complete ? "ok" : "needs");
        })
            .catch(() => {
            // If the profile call fails, don't block — fail open.
            if (!cancelled)
                setStatus("ok");
        });
        return () => {
            cancelled = true;
        };
    }, []);
    if (status === "loading")
        return null;
    if (status === "needs" && location.pathname !== "/onboarding") {
        return _jsx(Navigate, { to: "/onboarding", replace: true });
    }
    return children;
}
// Subscribe to WebSocket pipeline events and enforce idle session timeout.
// Both hooks need to be inside the auth boundary so they only run when authed.
function AuthBoundaryHooks() {
    usePipelineEvents();
    useIdleTimeout();
    useLocaleInit();
    useGlobalCommandShortcut();
    return null;
}
export default function App() {
    return (_jsxs(ErrorBoundary, { children: [_jsxs(Routes, { children: [_jsx(Route, { path: "/login", element: _jsx(LoginPage, {}) }), _jsx(Route, { path: "/signup", element: _jsx(SignupPage, {}) }), _jsx(Route, { path: "/signup/education", element: _jsx(SignupEducationPage, {}) }), _jsx(Route, { path: "/disclosures", element: _jsx(DisclosuresPage, {}) }), _jsx(Route, { path: "/data-safety", element: _jsx(DataSafetyPage, {}) }), _jsx(Route, { path: "/onboarding", element: _jsx(RequireAuth, { children: _jsx(OnboardingPage, {}) }) }), _jsx(Route, { path: "/", element: _jsx(RequireAuth, { children: _jsx(RequireOnboarded, { children: _jsxs(_Fragment, { children: [_jsx(AuthBoundaryHooks, {}), _jsx(AgentsPage, {})] }) }) }) }), _jsx(Route, { path: "/xray", element: _jsx(RequireAuth, { children: _jsx(RequireOnboarded, { children: _jsx(XRayPage, {}) }) }) }), _jsx(Route, { path: "/artifacts", element: _jsx(RequireAuth, { children: _jsx(RequireOnboarded, { children: _jsx(ArtifactsPage, {}) }) }) }), _jsx(Route, { path: "/memory", element: _jsx(RequireAuth, { children: _jsx(RequireOnboarded, { children: _jsx(MemoryPage, {}) }) }) }), _jsx(Route, { path: "/agents/new", element: _jsx(RequireAuth, { children: _jsx(RequireOnboarded, { children: _jsx(AgentCreatorPage, {}) }) }) }), _jsx(Route, { path: "/agents/:agentId/edit", element: _jsx(RequireAuth, { children: _jsx(RequireOnboarded, { children: _jsx(AgentEditorPage, {}) }) }) }), _jsx(Route, { path: "/profile", element: _jsx(RequireAuth, { children: _jsx(RequireOnboarded, { children: _jsx(ProfilePage, {}) }) }) }), _jsx(Route, { path: "/guide", element: _jsx(RequireAuth, { children: _jsx(RequireOnboarded, { children: _jsx(GuidePage, {}) }) }) }), _jsx(Route, { path: "*", element: _jsx(Navigate, { to: "/", replace: true }) })] }), _jsx(CommandPaletteModal, {})] }));
}
